import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import { 
  ArrowLeft, 
  Megaphone,
  Heart,
  BookOpen,
  User,
  Send,
  Trash2
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

const CATEGORIES = {
  announcements: { label: 'Announcement', icon: Megaphone, color: 'bg-slate-700' },
  prayer_requests: { label: 'Prayer Request', icon: Heart, color: 'bg-amber-600' },
  bible_verses: { label: 'Bible Verse', icon: BookOpen, color: 'bg-teal-600' }
};

export default function ForumPost({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletePostDialogOpen, setDeletePostDialogOpen] = useState(false);
  const [deleteCommentDialogOpen, setDeleteCommentDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error fetching post:", error);
      toast.error("Failed to load post");
      navigate('/forum');
    } finally {
      setLoading(false);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please write a comment");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/posts/${id}/comments`, {
        content: newComment
      });
      toast.success("Comment added!");
      setNewComment('');
      fetchPost();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const deletePost = async () => {
    setDeletePostDialogOpen(true);
  };

  const confirmDeletePost = async () => {
    try {
      await axios.delete(`${API}/forum/posts/${id}`);
      toast.success("Post deleted");
      navigate('/forum');
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    } finally {
      setDeletePostDialogOpen(false);
    }
  };

  const deleteComment = async (commentId) => {
    setCommentToDelete(commentId);
    setDeleteCommentDialogOpen(true);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;
    
    try {
      await axios.delete(`${API}/forum/posts/${id}/comments/${commentToDelete}`);
      toast.success("Comment deleted");
      fetchPost();
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    } finally {
      setCommentToDelete(null);
      setDeleteCommentDialogOpen(false);
    }
  };

  const canDeleteComment = (comment) => {
    return user.role === 'teacher' || comment.author_id === user.user_id;
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout user={user}>
        <div className="text-center py-16">
          <p className="text-slate-600">Post not found</p>
          <Button variant="link" onClick={() => navigate('/forum')}>
            Back to Forum
          </Button>
        </div>
      </Layout>
    );
  }

  const category = CATEGORIES[post.category] || CATEGORIES.announcements;
  const canDelete = user.role === 'teacher' || post.author_id === user.user_id;

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/forum')}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Forum
          </Button>
          {canDelete && (
            <Button 
              variant="ghost" 
              onClick={deletePost}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>

        {/* Post Content */}
        <Card className="border-slate-100">
          <CardContent className="p-8">
            {/* Category Badge */}
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 ${category.color} rounded-xl flex items-center justify-center`}>
                <category.icon className="w-5 h-5 text-white" />
              </div>
              <Badge variant="outline">{category.label}</Badge>
            </div>

            {/* Title */}
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              {post.title}
            </h1>

            {/* Author Info */}
            <div className="flex items-center gap-3 mb-6 text-sm text-slate-600">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-slate-200 text-slate-700 text-xs">
                  {getInitials(post.author_name)}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-slate-900">{post.author_name}</span>
              <span>•</span>
              <span>{format(new Date(post.created_at), 'MMMM d, yyyy \'at\' h:mm a')}</span>
            </div>

            {/* Content */}
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed text-lg">
                {post.content}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Comments Section */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg">
              Comments ({post.comments?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* New Comment Form */}
            <div className="flex gap-3">
              <Avatar className="w-10 h-10 flex-shrink-0">
                <AvatarFallback className="bg-amber-100 text-amber-700">
                  {getInitials(user.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Textarea
                  data-testid="comment-input"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="min-h-[80px] mb-3"
                />
                <Button
                  data-testid="submit-comment-btn"
                  onClick={submitComment}
                  disabled={submitting}
                  className="bg-slate-800 hover:bg-slate-900 rounded-full"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Posting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Post Comment
                    </>
                  )}
                </Button>
              </div>
            </div>

            {post.comments?.length > 0 && <Separator />}

            {/* Comments List */}
            <div className="space-y-6">
              {post.comments?.map((comment) => (
                <div key={comment.comment_id} className="flex gap-3">
                  <Avatar className="w-10 h-10 flex-shrink-0">
                    <AvatarFallback className="bg-slate-100 text-slate-700">
                      {getInitials(comment.author_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900">{comment.author_name}</span>
                        <span className="text-xs text-slate-500">
                          {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      {canDeleteComment(comment) && (
                        <button
                          type="button"
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            deleteComment(comment.comment_id);
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            deleteComment(comment.comment_id);
                          }}
                          className="text-slate-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100 h-8 w-8 p-0 rounded-full flex items-center justify-center transition-colors"
                          title="Delete comment"
                          style={{ 
                            cursor: 'pointer',
                            WebkitTapHighlightColor: 'transparent',
                            touchAction: 'manipulation'
                          }}
                        >
                          <Trash2 className="w-4 h-4" style={{ pointerEvents: 'none' }} />
                        </button>
                      )}
                    </div>
                    <p className="text-slate-700">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>

            {post.comments?.length === 0 && (
              <p className="text-center text-slate-500 py-4">
                No comments yet. Be the first to share your thoughts!
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Post Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deletePostDialogOpen}
        onOpenChange={setDeletePostDialogOpen}
        onConfirm={confirmDeletePost}
        title="Delete Post?"
        description="This will permanently delete this forum post and all its comments. This action cannot be undone."
        confirmText="Delete Post"
      />

      {/* Delete Comment Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteCommentDialogOpen}
        onOpenChange={setDeleteCommentDialogOpen}
        onConfirm={confirmDeleteComment}
        title="Delete Comment?"
        description="This will permanently delete this comment. This action cannot be undone."
        confirmText="Delete Comment"
      />
    </Layout>
  );
}
